import { mat4, vec3 } from 'gl-matrix';
import { PropertyAnimation } from '../../animation';
import { Camera } from '../../camera';
import { Context } from '../../context/Context';
import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../../renderer';
import { LiquidSurfaceShaderSettings } from '../../shader/generated';
import { Texture } from '../../texture';
import { ObjectInteraction } from '../ObjectManager';
import { bindTransparentLighting, findCsmLight } from '../shaderBindingHelpers';

export type LiquidSurfaceConfig = {
  color?: [number, number, number, number];
  opacity?: number;
  amplitude?: number;
  waveSpeed?: number;
  waveDirection?: [number, number];
  texture?: Texture;
  shoreFadeDistance?: number;
  /** Scene-depth shore fade radius (m). Fades alpha to 0 where the opaque
   * scene depth approaches the water surface — hides the boundary between
   * a sloppy authored polygon and the terrain/walls/boulders behind it.
   * 0 disables. */
  shoreDepthFade?: number;
  /** PBR roughness for the per-light specular glint. 0.02 = mirror-tight
   * pinpoint sun glare, 0.5 = broad soft highlight. Combined with the wave
   * normals this drives the "glistering" of the sun on the surface. */
  glintRoughness?: number;
};

type LiquidGeometry = {
  positions: Float32Array;
  texCoords: Float32Array;
  indices: Uint16Array | Uint32Array;
  edgeDistance?: Float32Array;
};

const defaults: Required<Omit<LiquidSurfaceConfig, 'texture'>> = {
  color: [0.1, 0.3, 0.6, 0.7],
  opacity: 0.6,
  amplitude: 0.15,
  waveSpeed: 1.0,
  waveDirection: [1.0, 0.6],
  shoreFadeDistance: 0,
  shoreDepthFade: 0,
  glintRoughness: 0.05,
};

export class LiquidSurface implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    static: true,
    culling: 'none',
    shadow: 'none',
    order: 2000,
    modes: [RenderMode.Transparent],
  };

  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  private vao: WebGLVertexArrayObject;
  // Retain buffer JS wrappers so V8 GC doesn't drop them — Chrome deletes the
  // underlying GL buffer when the wrapper is collected, leaving the VAO's
  // attribute bindings dangling and the next drawElements fails with
  // "Insufficient buffer size".
  private buffers: WebGLBuffer[] = [];
  private indexCount: number;
  private indexType: number;
  private _modelMatrix: mat4 = mat4.create();

  public color: [number, number, number, number];
  public opacity: number;
  public amplitude: number;
  public waveSpeed: number;
  public waveDirection: [number, number];
  public texture?: Texture;
  public shoreFadeDistance: number;
  public shoreDepthFade: number;
  public glintRoughness: number;

  // ── Factory methods ────────────────────────────────────────────────

  static createRectangle(
    ctx: Context,
    width: number,
    depth: number,
    config?: LiquidSurfaceConfig,
    resolution: number = 32,
  ): LiquidSurface {
    const cols = resolution;
    const rows = resolution;
    const vertexCount = (cols + 1) * (rows + 1);
    const positions = new Float32Array(vertexCount * 3);
    const texCoords = new Float32Array(vertexCount * 2);
    const indices = new Uint16Array(cols * rows * 6);

    let vi = 0;
    let ti = 0;
    for (let r = 0; r <= rows; r++) {
      for (let c = 0; c <= cols; c++) {
        const x = (c / cols - 0.5) * width;
        const z = (r / rows - 0.5) * depth;
        positions[vi++] = x;
        positions[vi++] = 0;
        positions[vi++] = z;
        texCoords[ti++] = c / cols;
        texCoords[ti++] = r / rows;
      }
    }

    let ii = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const tl = r * (cols + 1) + c;
        const tr = tl + 1;
        const bl = tl + (cols + 1);
        const br = bl + 1;
        indices[ii++] = tl;
        indices[ii++] = bl;
        indices[ii++] = tr;
        indices[ii++] = tr;
        indices[ii++] = bl;
        indices[ii++] = br;
      }
    }

    // Procedural shapes have no real shoreline — fill with a sentinel so the
    // shader's smoothstep returns 1.0 (no fade) regardless of uShoreFadeDistance.
    const edgeDistance = new Float32Array(vertexCount).fill(1e6);

    return new LiquidSurface(ctx, { positions, texCoords, indices, edgeDistance }, config);
  }

  static createCircle(
    ctx: Context,
    radius: number,
    config?: LiquidSurfaceConfig,
    segments: number = 32,
    rings: number = 16,
  ): LiquidSurface {
    const vertexCount = 1 + rings * segments;
    const positions = new Float32Array(vertexCount * 3);
    const texCoords = new Float32Array(vertexCount * 2);

    // Center vertex
    positions[0] = 0;
    positions[1] = 0;
    positions[2] = 0;
    texCoords[0] = 0.5;
    texCoords[1] = 0.5;

    let vi = 3;
    let ti = 2;
    for (let r = 1; r <= rings; r++) {
      const ringRadius = (r / rings) * radius;
      for (let s = 0; s < segments; s++) {
        const angle = (s / segments) * Math.PI * 2;
        const x = Math.cos(angle) * ringRadius;
        const z = Math.sin(angle) * ringRadius;
        positions[vi++] = x;
        positions[vi++] = 0;
        positions[vi++] = z;
        texCoords[ti++] = (Math.cos(angle) * (r / rings) + 1) / 2;
        texCoords[ti++] = (Math.sin(angle) * (r / rings) + 1) / 2;
      }
    }

    // Indices: center fan + quad strips between rings
    const triCount = segments + (rings - 1) * segments * 2;
    const indices = new Uint16Array(triCount * 3);
    let ii = 0;

    // Center fan (ring 0 → ring 1) — CCW winding for +Y facing
    for (let s = 0; s < segments; s++) {
      const next = (s + 1) % segments;
      indices[ii++] = 0;
      indices[ii++] = 1 + next;
      indices[ii++] = 1 + s;
    }

    // Quad strips between rings — CCW winding for +Y facing
    for (let r = 1; r < rings; r++) {
      const innerStart = 1 + (r - 1) * segments;
      const outerStart = 1 + r * segments;
      for (let s = 0; s < segments; s++) {
        const next = (s + 1) % segments;
        indices[ii++] = innerStart + s;
        indices[ii++] = innerStart + next;
        indices[ii++] = outerStart + s;
        indices[ii++] = innerStart + next;
        indices[ii++] = outerStart + next;
        indices[ii++] = outerStart + s;
      }
    }

    // Procedural shapes have no real shoreline — fill with a sentinel so the
    // shader's smoothstep returns 1.0 (no fade) regardless of uShoreFadeDistance.
    const edgeDistance = new Float32Array(vertexCount).fill(1e6);

    return new LiquidSurface(
      ctx,
      { positions, texCoords, indices: indices.subarray(0, ii), edgeDistance },
      config,
    );
  }

  /**
   * Build a LiquidSurface from pre-baked geometry (e.g. backend-baked liquid volume bundle).
   * Accepts either Uint16Array or Uint32Array indices — large baked meshes overflow Uint16.
   * Vertices are expected to be in world space; callers should leave the model matrix at identity.
   */
  static fromGeometry(
    ctx: Context,
    geometry: LiquidGeometry,
    config?: LiquidSurfaceConfig,
  ): LiquidSurface {
    return new LiquidSurface(ctx, geometry, config);
  }

  // ── Constructor ────────────────────────────────────────────────────

  constructor(
    private ctx: Context,
    geometry: LiquidGeometry,
    config?: LiquidSurfaceConfig,
  ) {
    this.color = config?.color ?? [...defaults.color];
    this.opacity = config?.opacity ?? defaults.opacity;
    this.amplitude = config?.amplitude ?? defaults.amplitude;
    this.waveSpeed = config?.waveSpeed ?? defaults.waveSpeed;
    this.waveDirection = config?.waveDirection ?? [...defaults.waveDirection];
    this.texture = config?.texture;
    this.shoreFadeDistance = config?.shoreFadeDistance ?? defaults.shoreFadeDistance;
    this.shoreDepthFade = config?.shoreDepthFade ?? defaults.shoreDepthFade;
    this.glintRoughness = config?.glintRoughness ?? defaults.glintRoughness;

    this.indexCount = geometry.indices.length;
    this.indexType =
      geometry.indices instanceof Uint32Array ? ctx.gl.UNSIGNED_INT : ctx.gl.UNSIGNED_SHORT;
    this.vao = this.createVAO(geometry);
  }

  // ── Public API ─────────────────────────────────────────────────────

  public destroy(): void {
    const { gl } = this.ctx;
    gl.deleteVertexArray(this.vao);
    for (const buf of this.buffers) gl.deleteBuffer(buf);
  }

  public setPosition(x: number, y: number, z: number): void {
    mat4.fromTranslation(this._modelMatrix, vec3.fromValues(x, y, z));
  }

  public get modelMatrix(): mat4 {
    return this._modelMatrix;
  }

  // ── DrawableObject ─────────────────────────────────────────────────

  public render(ctx: Context, camera: Camera, settings: RenderSettings): void {
    const gl = ctx.gl;
    const csmLight = findCsmLight(settings);

    const depthFadeActive =
      this.shoreDepthFade > 0 && settings.sceneDepthTexture != null && settings.invViewProjection != null;
    const shaderSettings: LiquidSurfaceShaderSettings = [];
    if (csmLight) shaderSettings.push('SHADOW_CSM');
    if (settings.volumetricTexture) shaderSettings.push('VOLUMETRIC');
    if (this.texture) shaderSettings.push('USE_TEXTURE');
    if (depthFadeActive) shaderSettings.push('DEPTH_SHORE_FADE');
    const shader = ctx.engine.shaderManager.getLiquidSurfaceShader(...shaderSettings);
    ctx.setShader(shader);

    gl.bindVertexArray(this.vao);

    const modelViewMatrix = mat4.multiply(mat4.create(), camera.modelViewMatrix, this._modelMatrix);
    const time = performance.now() / 1000;

    shader.uniformsContainer
      .setModelMatrix(this._modelMatrix)
      .setModelViewMatrix(modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setTime(time)
      .setAmplitude(this.amplitude)
      .setWaveSpeed(this.waveSpeed)
      .setWaveDirection(this.waveDirection)
      .setLiquidColor(this.color)
      .setOpacity(this.opacity)
      .setAmbientColor(ctx.engine.activeScene!.lightManager.ambientColor)
      .setCameraPosition(camera.cameraPosition)
      .setShoreFadeDistance(this.shoreFadeDistance)
      .setGlintRoughness(this.glintRoughness);

    if (this.texture) {
      shader.uniformsContainer.setTextureSampler(this.texture);
    }

    if (depthFadeActive) {
      shader.uniformsContainer
        .setSceneDepth(settings.sceneDepthTexture!)
        .setInvViewProjection(settings.invViewProjection!)
        .setShoreDepthFade(this.shoreDepthFade);
      if (settings.screenSize) {
        shader.uniformsContainer.setScreenSize(settings.screenSize);
      }
    }

    bindTransparentLighting(shader.uniformsContainer, settings, camera, csmLight);

    gl.drawElements(gl.TRIANGLES, this.indexCount, this.indexType, 0);

    // Unbind so other code that touches ELEMENT_ARRAY_BUFFER without first
    // binding their own VAO doesn't silently rewrite our element binding.
    gl.bindVertexArray(null);
  }

  // ── Private ────────────────────────────────────────────────────────

  private createVAO(geometry: LiquidGeometry): WebGLVertexArrayObject {
    const gl = this.ctx.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);

    // Position buffer — location 0
    const posBuffer = gl.createBuffer()!;
    this.buffers.push(posBuffer);
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, geometry.positions, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);

    // Tex coord buffer — location 2
    const tcBuffer = gl.createBuffer()!;
    this.buffers.push(tcBuffer);
    gl.bindBuffer(gl.ARRAY_BUFFER, tcBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, geometry.texCoords, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, 0, 0);

    // Edge distance buffer — location 3, only bound when the caller actually
    // has per-vertex shore distances (baked liquid volumes). Preview rectangles
    // leave it disabled; the shader reads the generic attrib default (0), and
    // the preview path forces uShoreFadeDistance=0 so aEdgeDistance is unused.
    if (geometry.edgeDistance) {
      const edBuffer = gl.createBuffer()!;
      this.buffers.push(edBuffer);
      gl.bindBuffer(gl.ARRAY_BUFFER, edBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, geometry.edgeDistance, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(3);
      gl.vertexAttribPointer(3, 1, gl.FLOAT, false, 0, 0);
    }

    // Index buffer
    const idxBuffer = gl.createBuffer()!;
    this.buffers.push(idxBuffer);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, geometry.indices, gl.STATIC_DRAW);

    gl.bindVertexArray(null);
    return vao;
  }
}
